import React from 'react';
import SlotMachine from './SlotMachine';
import { useTheme, ThemedCard, ThemedButton, ThemedContainer } from './ThemeContext';
import MoveHistory from './MoveHistory';
import { Volume2, VolumeX, RefreshCw } from 'lucide-react';
import { useSound } from './SoundManager';

const TIME_CONTROLS = [
  { time: 3, increment: 2, label: '3|2' },
  { time: 5, increment: 2, label: '5|2' },
  { time: 10, increment: 2, label: '10|2' },
];

const BotSlotConfig = ({ botSlotConfig }) => {
  const { woodStyles } = useTheme();

  const getSlotLabel = (slotType) => {
    if (slotType === 'random') return 'Random Move';
    return `Stockfish Line ${slotType}`;
  };

  return (
    <div className="p-3 mb-2 text-sm rounded-lg" style={{ background: 'rgba(0,0,0,0.1)' }}>
      <div className={`font-semibold mb-1 ${woodStyles.text.primary}`}>Bot Configuration:</div>
      <div className="grid grid-cols-3 gap-2">
        <div className={`px-2 py-1 rounded text-center ${woodStyles.surface.secondary}`}>
          <div className={`text-xs ${woodStyles.text.secondary}`}>Slot 1</div>
          <div className={`${woodStyles.text.primary}`}>{getSlotLabel(botSlotConfig?.slot1 || 'random')}</div>
        </div>
        <div className={`px-2 py-1 rounded text-center ${woodStyles.surface.secondary}`}>
          <div className={`text-xs ${woodStyles.text.secondary}`}>Slot 2</div>
          <div className={`${woodStyles.text.primary}`}>{getSlotLabel(botSlotConfig?.slot2 || 'random')}</div>
        </div>
        <div className={`px-2 py-1 rounded text-center ${woodStyles.surface.secondary}`}>
          <div className={`text-xs ${woodStyles.text.secondary}`}>Slot 3</div>
          <div className={`${woodStyles.text.primary}`}>{getSlotLabel(botSlotConfig?.slot3 || 'random')}</div>
        </div>
      </div>
    </div>
  );
};

export const GameInfoPanel = ({ game, user, handlers, timers }) => {
  const { woodStyles } = useTheme();
  const { isMuted, toggleMute } = useSound();

  // Check if this is a local game
  const isLocalGame = game?.isLocalGame || false;

  const renderPlayerInfo = (color) => {
    const isCurrentPlayer = handlers.playerColor === color;
    const player = game?.players?.[color];
    const isPlayersTurn = game?.turn === 'w' ? 'white' : 'black' === color;

    // Timer display logic based on game state and viewing mode
    const getTimeDisplay = () => {
      // Skip time display for local games
      if (isLocalGame) {
        return null;
      }

      const isGameActive = game?.status === 'active';
      const isGameCompleted = game?.status === 'completed';
      const isViewingHistoricalMove = handlers.currentMoveIndex !== -1 &&
        handlers.currentMoveIndex !== (game?.moves?.length - 1);

      // For active games, use live timer
      // For completed games in review mode, use historical time
      // Otherwise, use current timer value
      let timeRemaining;

      if (isGameActive && !isViewingHistoricalMove) {
        // Active game, current position - use live timer
        timeRemaining = timers?.format(timers[color]);
      } else if (isGameCompleted && isViewingHistoricalMove) {
        // Completed game, historical position - use recorded time
        timeRemaining = timers?.format(timers[color]);
      } else {
        // Default case
        timeRemaining = timers?.format(timers[color]);
      }

      return timeRemaining;
    };

    const timeRemaining = getTimeDisplay();

    return (
      <ThemedContainer className="w-full h-full">
        <div className="flex items-center justify-between px-3 py-1.5">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center
              ${woodStyles.surface.secondary} border border-opacity-20`}
              style={{ borderColor: woodStyles.borderColor }}
            >
              <span className="text-base">👤</span>
            </div>
            <div className="flex items-center gap-1">
              <div>
                <div className={`text-sm ${woodStyles.text.primary} flex items-center gap-1`}>
                  {isCurrentPlayer ? `You` : (player?.username || "Anonymous")}
                  {!isLocalGame && ((handlers.drawOffer?.from === player?.id) ||
                    (handlers.regularOffer?.from === player?.id)) && (
                      <div
                        onClick={handlers.onScrollToStatus}
                        className={`px-1.5 py-0.5 rounded-full shadow-sm 
                        flex items-center gap-1 cursor-pointer transform transition-all scale-90
                        ${woodStyles.button.primary} hover:scale-100`}
                      >
                        <div className="animate-[wiggle_1s_ease-in-out_infinite]">
                          🔔
                        </div>
                        <span className="text-xs text-white whitespace-nowrap">
                          {handlers.drawOffer ? "Draw Offered" : "Regular Game Offered"}
                        </span>
                      </div>
                    )}
                </div>
                <div className={`text-xs ${woodStyles.text.secondary}`}>
                  Playing as {color}
                </div>
              </div>
            </div>
          </div>

          {/* Timer display - only for network games */}
          {!isLocalGame && timeRemaining && (
            <div className={`px-2 py-1 rounded-lg ${woodStyles.surface.primary}`}>
              <div className={`text-xs ${woodStyles.text.secondary} text-center`}>Time</div>
              <div className="font-mono text-base w-16 text-center">
                <span className={game?.status === 'completed'
                  ? woodStyles.text.primary
                  : isPlayersTurn
                    ? "text-red-500 font-bold"
                    : woodStyles.text.accent}
                >
                  {timeRemaining}
                </span>
              </div>
            </div>
          )}
        </div>

        {!handlers.isRegularGame && handlers.status === 'active' && (
          <div className="px-3 py-1">
            <SlotMachine
              slots={handlers.slots[color]}
              onSlotClick={!handlers.isSpectator && isCurrentPlayer ? handlers.onSlotClick : undefined}
              selectedSlot={isCurrentPlayer ? handlers.selectedSlot : undefined}
              isSpinning={handlers.isSpinning[color]}
              finMove={handlers.finMoves}
              showGoldenBorder={isCurrentPlayer && handlers.showGoldenBorder}
              onReset={!handlers.isSpectator && isCurrentPlayer ? handlers.onResetSlots : undefined}
              onRandomMove={!handlers.isSpectator && isCurrentPlayer ? handlers.onRandomMove : undefined}
              onSubmit={handlers.onSubmitMove}
              anarchyMode={game?.isAnarchy}
              message={handlers.messages.anarchyMessage || {
                text: handlers.messages.forcedMoveMessage,
                color: "bg-yellow-500",
              }}
              disabled={!game?.players?.[color]?.id === user.id ||
                game?.status !== 'active' ||
                game?.turn !== color[0] ||
                handlers.isSpinning[color]}
              canSubmit={!handlers.isSpinning[color] &&
                (handlers.slots[color].filter(Boolean).length === handlers.uniqueMoveLimit) && // Require all 3 slots to be filled
                game?.players?.[color]?.id === user.id &&
                game?.turn === color[0]}
              isOpponent={!isCurrentPlayer}
              possibleMoves={handlers.currentPossibleMoves}
              isSpectating={handlers.isSpectator}
              color={color}
              currentColor={handlers.currentTurn}
            />
          </div>
        )}
      </ThemedContainer>
    );
  };

  const renderGameStatus = () => {
    if (!game) return null;

    // For local games, show bot configuration
    if (isLocalGame && game.botSlotConfig) {
      return (
        <div className="text-center space-y-2">
          <BotSlotConfig botSlotConfig={game.botSlotConfig} />

          {handlers.messages.anarchyMessage || handlers.messages.forcedMoveMessage ? (
            <div className={`text-lg font-bold ${handlers.messages.anarchyMessage?.color || 'text-yellow-500'}`}>
              {handlers.messages.anarchyMessage?.text || handlers.messages.forcedMoveMessage}
            </div>
          ) : null}

          {game.status === "completed" && (
            <div className={`text-xl font-bold ${woodStyles.text.primary}`}>
              {game.endReason === 'draw' || game.endReason === 'stalemate'
                ? `Game Over - Drawn by ${game.endReason}`
                : `Game Over - ${game.winner === handlers.playerColor ? 'You win' : 'Bot wins'}
                   ${game.endReason === "checkmate" ? " by checkmate" : ""}
                   ${game.endReason === "resignation" ? " by resignation" : ""}`
              }
            </div>
          )}
        </div>
      );
    }

    if (handlers.messages.anarchyMessage || handlers.messages.forcedMoveMessage) {
      return (
        <div className="text-center space-y-2">
          <div className={`text-lg font-bold ${handlers.messages.anarchyMessage?.color || 'text-yellow-500'}`}>
            {handlers.messages.anarchyMessage?.text || handlers.messages.forcedMoveMessage}
          </div>
        </div>
      );
    }

    if (game.status === "waiting") {
      return (
        <div className="text-center">
          <div className={`text-xl mb-4 ${woodStyles.text.accent}`}>
            Waiting for opponent...
          </div>
          {game.creator?.id === user.id && (
            <ThemedButton onClick={handlers.onAbortGame} variant="secondary">
              Abort Game
            </ThemedButton>
          )}
        </div>
      );
    }

    if (handlers.countdownTimer !== null) {
      return (
        <div className="text-center">
          <div className={`text-2xl font-bold ${woodStyles.text.primary}`}>
            Regular Chess starting in {handlers.countdownTimer}...
          </div>
        </div>
      );
    }

    if (game.status === "completed") {
      const reason = game.endReason;
      const isDrawn = ["stalemate", "agreement", "threefold repetition",
        "fifty-move rule", "fifty-move rule and threefold repetition"].includes(reason);

      return (
        <div className="text-center">
          <div className={`text-xl font-bold ${woodStyles.text.primary}`}>
            {isDrawn
              ? `Game Over - Drawn by ${reason}`
              : `Game Over - ${game.winner?.username || (game.winner?.id === game.players?.white?.id ? 'White' : 'Black')} wins
                 ${reason === "checkmate" ? " by checkmate" : ""}
                 ${reason === "timeout" ? " by timeout" : ""}
                 ${reason === "resignation" ? " by resignation" : ""}`
            }
          </div>
        </div>
      );
    }

    if (handlers.regularOffer || handlers.drawOffer) {
      const offer = handlers.regularOffer || handlers.drawOffer;
      const isDrawOffer = !handlers.regularOffer;

      if (handlers.isSpectator) {
        return (
          <div className="text-center">
            <div className={`text-lg ${woodStyles.text.primary}`}>
              {game.players[offer.from === game.players.white.id ? 'white' : 'black'].username}
              {isDrawOffer ? ' offered a draw' : ` offered to switch to regular chess (${handlers.regularOffer.timeControl})`}
            </div>
          </div>
        );
      }

      if (offer.from === user.id) {
        return (
          <div className="text-center">
            <div className={`text-lg ${woodStyles.text.primary}`}>
              Waiting for opponent to respond to {isDrawOffer ? 'draw' : `regular chess (${handlers.regularOffer.timeControl})`} offer...
            </div>
          </div>
        );
      }

      return (
        <div className="text-center space-y-4">
          <div className={`text-lg ${woodStyles.text.primary}`}>
            {game.players[offer.from === game.players.white.id ? 'white' : 'black'].username}
            {isDrawOffer ? ' offers a draw' : ` offers to switch to regular chess (${handlers.regularOffer.timeControl})`}
          </div>
          {!handlers.isSpectator && (
            <div className="flex justify-center space-x-4">
              <ThemedButton
                onClick={() => isDrawOffer ? handlers.onDrawResponse(true) : handlers.onRegularGameResponse(true)}
                variant="primary"
              >
                Accept
              </ThemedButton>
              <ThemedButton
                onClick={() => isDrawOffer ? handlers.onDrawResponse(false) : handlers.onRegularGameResponse(false)}
                variant="secondary"
              >
                Decline
              </ThemedButton>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`${game?.turn === handlers.playerColor?.[0] ? woodStyles.text.accent : woodStyles.text.secondary}`}>
        {handlers.isSpectator
          ? `${game.players[game.turn === 'w' ? 'white' : 'black'].username}'s turn`
          : game?.turn === handlers.playerColor?.[0]
            ? "Your turn"
            : isLocalGame
              ? "Bot is thinking..."
              : `Waiting for ${game.players[game.turn === 'w' ? 'white' : 'black'].username}'s move`}
      </div>
    );
  };

  const renderGameControls = () => {
    return (
      <div className="flex flex-col gap-4">
        {/* Top control buttons row */}
        <div className="flex justify-between items-center">
          {/* Sound toggle button with tooltip */}
          <div className="relative group">
            <ThemedButton
              onClick={toggleMute}
              variant="secondary"
              className="w-12 h-12 flex items-center justify-center"
              aria-label={isMuted ? "Unmute sounds" : "Mute sounds"}
            >
              {isMuted ? (
                <VolumeX className={`w-6 h-6 ${woodStyles.text.primary}`} />
              ) : (
                <Volume2 className={`w-6 h-6 ${woodStyles.text.primary}`} />
              )}
            </ThemedButton>
            {/* Tooltip */}
            <div className="absolute left-1/2 top-full transform -translate-x-1/2 mt-2 px-3 py-1 
                           text-sm rounded-md shadow-lg opacity-0 group-hover:opacity-100 
                           transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50"
              style={{
                background: woodStyles.background,
                borderColor: woodStyles.borderColor,
                borderWidth: '1px'
              }}>
              <span className={woodStyles.text.primary}>{isMuted ? "Unmute" : "Mute"}</span>
            </div>
          </div>

          {/* Flip board button with tooltip */}
          <div className="relative group">
            <ThemedButton
              onClick={handlers.onFlipBoard}
              variant="secondary"
              className="w-12 h-12 flex items-center justify-center"
              aria-label="Flip board"
            >
              <RefreshCw className={`w-6 h-6 ${woodStyles.text.primary}`} />
            </ThemedButton>
            {/* Tooltip */}
            <div className="absolute left-1/2 top-full transform -translate-x-1/2 mt-2 px-3 py-1 
                           text-sm rounded-md shadow-lg opacity-0 group-hover:opacity-100 
                           transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50"
              style={{
                background: woodStyles.background,
                borderColor: woodStyles.borderColor,
                borderWidth: '1px'
              }}>
              <span className={woodStyles.text.primary}>Flip board</span>
            </div>
          </div>
        </div>

        {/* Game action buttons - only for players, not spectators */}
        {!handlers.isSpectator && (
          <div className="flex flex-wrap gap-2 justify-center">
            {/* Show resign button for all games */}
            <ThemedButton
              onClick={handlers.onResign}
              disabled={
                game?.status !== 'active' ||
                game?.turn !== handlers.playerColor?.[0]
              }
              variant="secondary"
            >
              Resign
            </ThemedButton>

            {/* Draw buttons only for network games */}
            {!isLocalGame && (
              <>
                <ThemedButton
                  onClick={handlers.onDrawOffer}
                  disabled={
                    game?.status !== 'active' ||
                    game?.turn !== handlers.playerColor?.[0] ||
                    handlers.drawOffer
                  }
                  variant="secondary"
                >
                  Offer Draw
                </ThemedButton>

                {handlers.canClaimDraw && (
                  <ThemedButton
                    onClick={handlers.onDrawClaim}
                    disabled={
                      game?.status !== 'active' ||
                      game?.turn !== handlers.playerColor?.[0]
                    }
                    variant="secondary"
                  >
                    Claim Draw
                  </ThemedButton>
                )}
              </>
            )}
          </div>
        )}

        {/* Regular game offers - only for network games */}
        {!isLocalGame && !handlers.isSpectator &&
          !handlers.isRegularGame &&
          game?.status === 'active' &&
          game?.moves?.length >= 20 && (
            <div className="flex flex-col gap-2">
              <div className={`text-sm font-medium ${woodStyles.text.secondary}`}>
                Switch to regular chess:
              </div>
              <div className="grid grid-cols-3 gap-2">
                {TIME_CONTROLS.map((control) => (
                  <ThemedButton
                    key={control.label}
                    onClick={() => handlers.onRegularGameOffer(control.label)}
                    disabled={
                      game?.turn !== handlers.playerColor?.[0] ||
                      handlers.regularOffer
                    }
                    variant="secondary"
                    className="text-sm"
                  >
                    {control.label}
                  </ThemedButton>
                ))}
              </div>
            </div>
          )}

        {/* Move history panel */}
        <div className="flex-grow">
          <ThemedCard className="h-64 overflow-y-auto">
            <MoveHistory
              moves={game?.moves || []}
              onPositionChange={handlers.onPositionChange}
              currentPosition={handlers.currentMoveIndex}
              showUnselected={handlers.showUnselected}
              onToggleUnselected={handlers.onToggleUnselected}
            />
          </ThemedCard>
        </div>
      </div>
    );
  };

  return {
    renderPlayerInfo,
    renderGameStatus,
    renderGameControls
  };
};

export default GameInfoPanel;